import {
  Brush, Cake, Cpu, Dna, HeartPulse, IdCard, Info, PawPrint, Pill, Scale, Sparkles, Stethoscope, Syringe,
  Tag, TriangleAlert, Users, Utensils, Worm, type LucideIcon,
} from "lucide-react";

/**
 * De vaste componentbibliotheek van de samengestelde diertegels.
 *
 * <p>Uit het ontwerp van 30-09-2026. Het uitgangspunt daar: een tegelontwerp bepaalt alleen de
 * <em>presentatie</em>. Welk gegeven een onderdeel toont, welke snelle actie eraan hangt en welk recht
 * daarvoor nodig is, staat één keer vast — hier. Daardoor kan dezelfde bron in een ring, een halve
 * meter of een statuslijst staan zonder dat er per vorm een tweede waarheid ontstaat over wat "medicatie"
 * betekent of wie hem mag wijzigen.
 *
 * <p><b>Plus of potlood, en waarom dat verschil telt.</b> Een plus voegt een <em>registratie</em> toe
 * aan de historie: een weging, een toediening, een enting. Die blijft staan en is later terug te zien.
 * Een potlood wijzigt <em>vaste informatie</em>: een voedingsschema, een allergie, de dierenarts. Dat
 * overschrijft. Wie die twee door elkaar haalt, krijgt een historie waarin gisteren ineens iets anders
 * staat dan gisteren.
 *
 * <p>Hier staan geen diergegevens en geen opmaak: alleen wat een onderdeel ís. De tegels lezen deze
 * lijst; de waarden komen per component uit zijn eigen bron.
 */

/** Wat de snelle actie doet. */
export type ActieSoort =
  /** Voegt een registratie toe aan de historie; de oude blijft staan. */
  | "registratie"
  /** Wijzigt vaste informatie; de vorige waarde wordt overschreven. */
  | "wijziging";

/**
 * Waar een onderdeel over gaat, voor het filter in de tegelbouwer.
 *
 * <p>Drie groepen en geen twaalf losse: met twaalf onderdelen is een filter pas nuttig als het de lijst
 * echt korter maakt, en dit zijn de drie manieren waarop iemand zoekt -- wat moet er vandaag gebeuren,
 * wat is er medisch, en wie is dit dier.
 */
export type ComponentGroep = "dagelijks" | "medisch" | "identiteit";

export interface DierComponentDef {
  sleutel: string;
  /** i18n-sleutel van de naam op de tegel. */
  label: string;
  /**
   * i18n-sleutel van één regel die zegt wat erin staat.
   *
   * <p>In de bouwer staat alleen de naam, en "Voeding" vertelt niet of je het schema krijgt of het
   * laatste voermoment. Deze regel wel, en dat scheelt een tegel maken om erachter te komen.
   */
  uitleg: string;
  groep: ComponentGroep;
  icoon: LucideIcon;
  actie: ActieSoort;
  /**
   * Het recht dat nodig is om de snelle actie te gebruiken.
   *
   * <p>Leeg betekent: wie het dier mag zien, mag dit ook. Zonder het recht blijft het onderdeel
   * zichtbaar maar verdwijnt de knop — een tegel die informatie weglaat omdat je niet mag schrijven
   * leest als een gat.
   */
  recht?: string;
  /**
   * Alleen aanbieden als het pension per dier rekent.
   *
   * <p>Geldt voor de dagprijs: bij een tarief per ruimte of per reservering gaat die prijs niet over
   * dit dier, en dan hoort het onderdeel niet in de bouwer te staan.
   */
  alleenBijTariefPerDier?: boolean;
}

/**
 * De onderdelen, in de volgorde van de matrix in het ontwerp.
 *
 * <p>Twaalf uit de matrix, plus de zes vaste diervelden (oktober 2026). Nieuwe onderdelen kunnen er later bij zonder dat bestaande tegels iets hoeven: een
 * tegel bewaart welke sleutels hij toont, niet wat die sleutels betekenen.
 */
export const DIER_COMPONENTEN: DierComponentDef[] = [
  { sleutel: "gewicht", label: "dierTegel.component.gewicht", uitleg: "dierTegel.uitleg.gewicht", groep: "dagelijks", icoon: Scale, actie: "registratie" },
  {
    sleutel: "voeding",
    label: "dierTegel.component.voeding",
    uitleg: "dierTegel.uitleg.voeding",
    groep: "dagelijks",
    icoon: Utensils,
    actie: "wijziging",
    // Een voermoment bijschrijven mag iedereen (dat is verzorgerswerk), het schema wijzigen niet: dat
    // is de instructie voor iedereen die daarna voert. De api maakt die grens al.
    recht: "ROLE_ANIMAL_FEEDING_EDIT",
  },
  { sleutel: "medicatie", label: "dierTegel.component.medicatie", uitleg: "dierTegel.uitleg.medicatie", groep: "medisch", icoon: Pill, actie: "registratie" },
  { sleutel: "vaccinaties", label: "dierTegel.component.vaccinaties", uitleg: "dierTegel.uitleg.vaccinaties", groep: "medisch", icoon: Syringe, actie: "registratie" },
  { sleutel: "ontworming", label: "dierTegel.component.ontworming", uitleg: "dierTegel.uitleg.ontworming", groep: "medisch", icoon: Worm, actie: "registratie" },
  { sleutel: "ontvlooiing", label: "dierTegel.component.ontvlooiing", uitleg: "dierTegel.uitleg.ontvlooiing", groep: "medisch", icoon: Sparkles, actie: "registratie" },
  {
    sleutel: "allergieen",
    label: "dierTegel.component.allergieen",
    uitleg: "dierTegel.uitleg.allergieen",
    groep: "medisch",
    icoon: TriangleAlert,
    actie: "wijziging",
    // Lezen mag iedereen die het dier ziet -- wie gaat voeren moet weten waar het dier niet tegen kan.
    // Schrijven hangt aan het dier-schrijfrecht, net als in de api.
    recht: "ROLE_ANIMAL_EDIT",
  },
  { sleutel: "gedrag", label: "dierTegel.component.gedrag", uitleg: "dierTegel.uitleg.gedrag", groep: "identiteit", icoon: HeartPulse, actie: "wijziging" },
  { sleutel: "verzorging", label: "dierTegel.component.verzorging", uitleg: "dierTegel.uitleg.verzorging", groep: "dagelijks", icoon: Brush, actie: "registratie" },
  {
    sleutel: "chipPaspoort",
    label: "dierTegel.component.chipPaspoort",
    uitleg: "dierTegel.uitleg.chipPaspoort",
    groep: "identiteit",
    icoon: Cpu,
    actie: "wijziging",
    // Chip en paspoort staan op de basisinformatie van het dier, en die gaat achter het eerste
    // dier-schrijfrecht.
    recht: "ROLE_ANIMAL_EDIT",
  },
  { sleutel: "dierenarts", label: "dierTegel.component.dierenarts", uitleg: "dierTegel.uitleg.dierenarts", groep: "medisch", icoon: Stethoscope, actie: "wijziging" },
  {
    sleutel: "dagprijs",
    label: "dierTegel.component.dagprijs",
    uitleg: "dierTegel.uitleg.dagprijs",
    groep: "identiteit",
    icoon: IdCard,
    actie: "wijziging",
    // Tarief bekijken en tarief wijzigen zijn losse rechten; dit is het wijzigrecht.
    recht: "ROLE_TARIEF_EDIT",
    alleenBijTariefPerDier: true,
  },
  // De vaste gegevens van het dier. Alleen lezen: ze wijzigen op de basisgegevens, waar ook het
  // schrijfrecht hangt -- zie DierveldOnderdelen.
  { sleutel: "diersoort", label: "dierTegel.component.diersoort", uitleg: "dierTegel.uitleg.diersoort", groep: "identiteit", icoon: PawPrint, actie: "wijziging", recht: "ROLE_ANIMAL_EDIT" },
  { sleutel: "ras", label: "dierTegel.component.ras", uitleg: "dierTegel.uitleg.ras", groep: "identiteit", icoon: Dna, actie: "wijziging", recht: "ROLE_ANIMAL_EDIT" },
  { sleutel: "geslacht", label: "dierTegel.component.geslacht", uitleg: "dierTegel.uitleg.geslacht", groep: "identiteit", icoon: Users, actie: "wijziging", recht: "ROLE_ANIMAL_EDIT" },
  { sleutel: "leeftijd", label: "dierTegel.component.leeftijd", uitleg: "dierTegel.uitleg.leeftijd", groep: "identiteit", icoon: Cake, actie: "wijziging", recht: "ROLE_ANIMAL_EDIT" },
  { sleutel: "kenmerken", label: "dierTegel.component.kenmerken", uitleg: "dierTegel.uitleg.kenmerken", groep: "identiteit", icoon: Tag, actie: "wijziging", recht: "ROLE_ANIMAL_EDIT" },
  { sleutel: "bijzonderheden", label: "dierTegel.component.bijzonderheden", uitleg: "dierTegel.uitleg.bijzonderheden", groep: "identiteit", icoon: Info, actie: "wijziging", recht: "ROLE_ANIMAL_EDIT" },
];

export const COMPONENT_PER_SLEUTEL: Record<string, DierComponentDef> =
  Object.fromEntries(DIER_COMPONENTEN.map((c) => [c.sleutel, c]));

/**
 * Hoeveel onderdelen er hoogstens in één tegel passen.
 *
 * <p>Uit het ontwerp. Niet omdat er technisch een grens is, maar omdat een tegel met acht dingen erin
 * geen tegel meer is: dan leest niemand meer welke waarde bij welk kopje hoort.
 */
export const MAX_ONDERDELEN = 6;

/**
 * De status die een onderdeel kan tonen.
 *
 * <p>Drie standen en geen vierde: in orde, aandacht of verlopen. Een onderdeel dat niets te melden
 * heeft toont geen status — een groen vinkje bij "geen allergieën bekend" leest als een controle die
 * gedaan is, en dat is het niet.
 */
export type ComponentStatus = "inOrde" | "aandacht" | "verlopen";
